<template lang="pug">
	.ssml-lexicon-editor.flex-col.space-large
		ListView.full-width(ref="listView", initialView="table", :remoteSorting="true", :hideViewsSelection="true", :getTotal="(r) => r.TotalNumberOfRecords", 
			:getItems="(r) => r.Results", :getData="lexiconType === 1 ? $Data.loadCustomTNs : $Data.loadLexicons", valueExpr="Oid", :columns="columns", :filterRow="false", :showColumnHeaders="false", :searchVisible="false", :pageSize="6", :bottomPagerVisible="false")
			template(slot="left-actions")
				DxButton(icon="plus", type="normal", :text="$tCaption('Create')", @click="newLexicon")
				FileUploader(ref="fileUploader", :text="$tCaption('Upload')", icon="upload", @popup="fileSelected = false", @file-selected="onFileSelected" @upload="onNewLexiconFileUpload", :multiple="false", :dropText="$tCaption('Or drop xml file here to upload')", 
					:accept="['.xml', '.pls', '.json']", :uploadText="$tCaption('Upload new lexicon')")
					.flex-col(v-if="fileSelected")
						.font-small.upper.grey {{$tCaption('Lexicon name')}}
						DxTextBox(:value.sync="lexiconName")
			template(v-slot:name-template="{ data }")
				.flex-row.justify.align-center(:class="{ 'disabled-controls': data.__isLoading }")
					.flex-row.space.align-center
						fas(v-if="data.__isLoading", icon="spinner" :spin="true")
						span {{data.Name}}
					.flex-row.space.align-center(v-if="data.Owner && data.Owner.Oid === $auth.companyId()")
						LexiconEditorPopup(@save="v => onLexiconSave(data, v)", :url="data && data.LexiconFile && data.LexiconFile.AzureFileUrl", :lexiconName="data.Name", :lexiconBO="data")
						DxButton(icon="trash", type="danger", stylingMode="text", @click="removeLexicon(data)")
		Loader(v-if="isLoading", :fullscreen="true", text="")
		LexiconEditorPopup(ref="newLexiconEditor", @save="v => onLexiconSave(newLexiconBO, v)", :UIHidden="true", :allowSave="newLexiconBO && !!newLexiconBO.Name", :height="610", :lexiconName="newLexiconBO && newLexiconBO.Name", :lexiconBO="newLexiconBO")
		LexiconEditorPopup(v-if="addToLexicon", ref="addToLexiconEditor", :UIHidden="true", @save="v => onLexiconSave(addToLexicon.lexicon, v, true)", @cancel="$emit('close')",
			:url="addToLexicon.lexicon && addToLexicon.lexicon.LexiconFile && addToLexicon.lexicon.LexiconFile.AzureFileUrl", :lexiconName="addToLexicon.lexicon.Name", :lexiconBO="addToLexicon.lexicon")
</template>

<script>
	import _ from 'lodash';
	import DxButton from 'devextreme-vue/button';
	import DxTextBox from 'devextreme-vue/text-box';
	import ListView from 'app-core/ListView.vue';
	import FileUploader from 'app-core/FileUploader.vue';
	import Loader from 'app-core/Loader.vue';
	import LexiconEditorPopup from 'view/lexicon/LexiconEditorPopup.vue';

	export default {
		translationCategory: 'insertion.SSMLLexiconEditor',
		name: 'reading-rate-editor',
		components: { DxButton, DxTextBox, ListView, FileUploader, Loader, LexiconEditorPopup},
		props: ['lexiconType', 'addToLexicon'],
		data() {
			return {
				columns: [{ dataField: 'Oid', caption: this.$tCaption('Name'), cellTemplate: 'name-template' }],
				isLoading: false,
				fileName: null,
				fileSelected: false,
				base64: null,
				newLexiconBO: null
			};
		},		
		watch: {
			
		},
		mounted() {
			if (__DEBUG__)
				window.ssmlLexiconEditor = this;
			if (this.addToLexicon) {
				this.$refs.addToLexiconEditor.show({
					addPhonemeToLexicon: this.addToLexicon?.grapheme
				});
			}
		},
		methods: {
			newLexicon() {
				this.newLexiconBO = {
					Oid: null,
					Name: null,
					Company: {
						Oid: this.$auth.companyId()
					},
					LexiconType: this.lexiconType || 0,
					Owner: {
						Oid: this.$auth.companyId()
					}
				};
				this.$refs.newLexiconEditor.show();
			},
			async onLexiconSave(lexicon, string, close) {
				if (__DEBUG__)
					console.debug('onLexiconSave', { lexicon, string });
				const lexiconBlob = new Blob([string], { type: lexicon.LexiconType === 0 ? 'text/xml;charset=utf-8' : 'application/json;charset=utf-8' });
				const base64Uri = await this.$Data.splitDataUri(await this.$Data.blob2Base64(lexiconBlob));
				const base64Content = base64Uri?.base64;
				if (lexicon) {
					this.$set(lexicon, '__isLoading', (lexicon.__isLoading || 0) + 1);
					this.$forceUpdate();
				}

				const isNewLexicon = !lexicon?.Oid;
				if (isNewLexicon) {
					this.isLoading++;
				}
				try {
					_.assign(lexicon, (await this.$Data.postExternalApi('Lexicon/CreateUpdateLexicon', lexicon))?.Data);
					await this.$Data.postExternalApi('Lexicon/SaveFileToLexicon', {
						ObjectId: lexicon.Oid,
						FileExtension: lexicon.LexiconType === 0 ? 'xml' : 'json',
						FileBase64Content: base64Content
					}).finally(() => {
						this.$Data.getExternalApi('Lexicon/GetLexicon', `id=${lexicon.Oid}`).then(r => {
							this.$set(lexicon, 'LexiconFile', r?.LexiconFile);
						}).finally(() => lexicon.__isLoading--);
					});

					if (close) {
						this.$emit('close');
						return;
					}

					await saveFile;

					if (isNewLexicon && this.$refs.listView) {
						this.$refs.listView.reload();
						this.$emit('change');
					}
				} finally {
					if (isNewLexicon) {
						this.isLoading--;
					}
				}
			},
			removeLexicon(lexicon) {
				this.$confirmMessage({
					title: this.$tCaption('Confirm action', '@actions'),
					text: this.$tCaption('Do you want to delete the lexicon?'),
					handler: () => {
						this.isLoading++;
						this.$Data.getExternalApi('Lexicon/DeleteLexicon', `id=${lexicon.Oid}`).finally(() => {
							this.isLoading--;
							if (this.$refs.listView)
								this.$refs.listView.reload();
							this.$emit('change');
						});
					}
				});
			},
			onFileSelected(v) {
				if (v?.length) {
					if (__DEBUG__)
						console.debug('onNewLexiconFileUpload', { v });
					let fr = new FileReader;
					fr.readAsDataURL(v[0]);
					fr.onloadend = async () => {
						this.base64 = fr.result;
						this.fileName = v[0].name;
						const name = this.fileName.split('.');
						this.lexiconName = name.slice(0, name.length-1).join('.');
						this.fileSelected = true;
					}
				}
			},
			async onNewLexiconFileUpload() {
				this.isLoading++;
				try {
					const name = this.fileName.split('.');
					const lexicon = (await this.$Data.postExternalApi('Lexicon/CreateUpdateLexicon', {
						Oid: null,
						Name: this.lexiconName,
						Company: {
							Oid: this.$auth.companyId()
						},
						LexiconType: this.lexiconType || 0
					}))?.Data;

					const base64Content = this.base64.split(';base64,')[1];

					await this.$Data.postExternalApi('Lexicon/SaveFileToLexicon', {
						ObjectId: lexicon.Oid,
						FileExtension: name[name.length-1],
						FileBase64Content: base64Content
					});

					if (this.$refs.listView)
						this.$refs.listView.reload();

					this.$emit('change');
				} finally {
					this.isLoading--;
				}
			}
		}
	};
</script>
<style lang='scss'>
	.ssml-lexicon-editor {

	}
</style>
